// Bike frame sizing calculator, ported from the Warehouse theme.
// Size data comes from the product's sizing profile metafield and all copy
// from the theme locales, so the script only does the arithmetic.
if (!customElements.get('circles-sizing-calculator')) {
  class CirclesSizingCalculator extends HTMLElement {
    connectedCallback() {
      // Guard against re-initialising when Horizon moves the node around.
      if (this.initialized) return;

      this.config = this.parseJson('[data-sizing-config]');
      this.strings = this.parseJson('[data-sizing-strings]');
      if (!this.config || !this.strings) return;

      const filter = this.parseJson('[data-sizing-filter]');
      const allSizes = Array.isArray(this.config.sizes) ? this.config.sizes : [];
      const allowed = filter && Array.isArray(filter.allowed) ? filter.allowed : [];
      this.sizes = allowed.length ? allSizes.filter((size) => allowed.includes(size.label)) : allSizes;
      this.labels = (filter && filter.labels) || {};
      this.fit = this.config.fit_method || 'saddle_height';
      this.source = this.config.range_source || 'guide';

      this.details = this.querySelector('[data-sizing-details]');
      this.heightInput = this.querySelector('[data-height-input]');
      this.saddleInput = this.querySelector('[data-saddle-input]');
      this.pbhInput = this.querySelector('[data-pbh-input]');
      this.resultSize = this.querySelector('[data-result-size]');
      this.resultDetail = this.querySelector('[data-result-detail]');

      if (this.fit === 'pbh') {
        const pbh = this.config.pbh;
        if (!pbh || !this.pbhInput) return;
        this.setupRange('pbh', this.pbhInput, pbh.min_cm, pbh.max_cm, pbh.step_cm, pbh.default_cm, 'cm');
        this.pbhInput.addEventListener('input', () => this.update());
      } else {
        const height = this.config.height;
        if (!height || !this.heightInput) return;
        this.setupRange('height', this.heightInput, height.min_cm, height.max_cm, height.step_cm, height.default_cm, 'cm');
        this.heightInput.addEventListener('input', () => this.fromHeight());

        if (this.fit === 'saddle_height') {
          const saddle = this.config.saddle;
          if (!saddle || !this.saddleInput) return;
          this.setupRange('saddle', this.saddleInput, saddle.min_mm, saddle.max_mm, 1, saddle.min_mm, 'mm');
          this.saddleInput.addEventListener('input', () => this.update());
        }
      }

      // Open on desktop; collapsed on phones, where it would push the buy button far down.
      this.media = window.matchMedia('(max-width: 749px)');
      this.onBreakpoint = (event) => this.syncOpen(event.matches);
      this.media.addEventListener('change', this.onBreakpoint);
      this.syncOpen(this.media.matches);

      this.initialized = true;
      this.classList.add('circles-sizing--ready');
      this.fit === 'pbh' ? this.update() : this.fromHeight();
    }

    disconnectedCallback() {
      if (this.media && this.onBreakpoint) this.media.removeEventListener('change', this.onBreakpoint);
    }

    parseJson(selector) {
      const element = this.querySelector(selector);
      if (!element) return null;
      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        return null;
      }
    }

    setupRange(name, input, min, max, step, value, unit) {
      Object.assign(input, { min, max, step: step || 1, value });
      this.querySelector(`[data-${name}-min]`).textContent = `${min} ${unit}`;
      this.querySelector(`[data-${name}-max]`).textContent = `${max} ${unit}`;
    }

    syncOpen(isMobile) {
      if (this.details) this.details.open = !isMobile;
    }

    // Placeholders are [name]: a "}" inside a Liquid string ends the {{ }} tag.
    message(key, values = {}) {
      return Object.entries(values).reduce(
        (text, [name, value]) => text.replaceAll(`[${name}]`, value),
        this.strings[key] || ''
      );
    }

    label(size) {
      return this.labels[size.label] || size.label;
    }

    show(size, detail) {
      this.resultSize.textContent = size;
      this.resultDetail.textContent = detail;
      this.resultDetail.hidden = false;
    }

    showContact(detail) {
      this.show(this.strings.contact, detail);
    }

    formatNumber(value) {
      return Number.isInteger(value) ? value : value.toFixed(1);
    }

    // Saddle height follows the rider's height until the rider adjusts it.
    fromHeight() {
      if (this.fit === 'saddle_height') {
        const height = Number(this.heightInput.value);
        const saddle = this.config.saddle;
        const estimate = Math.round(height * Number(this.config.height.saddle_ratio));
        this.saddleInput.value = Math.min(saddle.max_mm, Math.max(saddle.min_mm, estimate));
      }
      this.update();
    }

    update() {
      if (!this.sizes.length) return this.showContact(this.strings.no_sizes);
      if (this.fit === 'pbh') return this.updatePbh();

      const height = Number(this.heightInput.value);
      const inches = Math.round(height / 2.54);
      this.querySelector('[data-height-value]').textContent =
        `${Math.floor(inches / 12)}'${inches % 12}"  ${this.formatNumber(height)} cm`;

      if (this.fit === 'rider_height') return this.updateRiderHeight(height);
      this.updateSaddleHeight();
    }

    updatePbh() {
      const pbh = Number(this.pbhInput.value);
      this.querySelector('[data-pbh-value]').textContent = `${this.formatNumber(pbh)} cm`;

      const eligible = this.sizes
        .filter((size) => Number.isFinite(Number(size.min_pbh_cm)) && pbh >= Number(size.min_pbh_cm))
        .sort((a, b) => Number(a.min_pbh_cm) - Number(b.min_pbh_cm));

      if (!eligible.length) {
        const min = Math.min(...this.sizes.map((size) => Number(size.min_pbh_cm)));
        return this.showContact(this.message('pbh_below_min', { min }));
      }

      // The largest size the rider clears is BASSI's first recommendation.
      const size = eligible[eligible.length - 1];
      this.show(this.label(size), this.message('pbh_result', { min: size.min_pbh_cm }));
    }

    updateRiderHeight(height) {
      const source = this.source === 'circles' ? 'circles' : 'guide';
      this.showMatches(
        height,
        'min_height_cm',
        'max_height_cm',
        'cm',
        `height_out_of_range_${source}`,
        `height_range_${source}`,
        `height_multiple_${source}`
      );
    }

    updateSaddleHeight() {
      const saddle = Number(this.saddleInput.value);
      this.querySelector('[data-saddle-value]').textContent = `${saddle} mm`;
      this.showMatches(saddle, 'min_saddle_mm', 'max_saddle_mm', 'mm', 'saddle_out_of_range', 'saddle_range', 'saddle_multiple');
    }

    showMatches(value, minKey, maxKey, unit, outOfRangeKey, rangeKey, multipleKey) {
      const matches = this.sizes.filter((size) => value >= Number(size[minKey]) && value <= Number(size[maxKey]));

      if (!matches.length) {
        const min = Math.min(...this.sizes.map((size) => Number(size[minKey])));
        const max = Math.max(...this.sizes.map((size) => Number(size[maxKey])));
        return this.showContact(this.message(outOfRangeKey, { min, max }));
      }

      const names = matches.map((size) => this.label(size)).join(' / ');

      if (matches.length === 1) {
        const [size] = matches;
        return this.show(names, this.message(rangeKey, { min: size[minKey], max: size[maxKey] }));
      }

      const ranges = matches
        .map((size) => `${this.label(size)}: ${size[minKey]}–${size[maxKey]} ${unit}`)
        .join(' / ');
      this.show(names, this.message(multipleKey, { ranges }));
    }
  }

  customElements.define('circles-sizing-calculator', CirclesSizingCalculator);
}
